import React from 'react';
import { MapPin } from 'lucide-react';

interface VenueMapProps {
  lat: number;
  lon: number;
  venueName: string;
  className?: string;
}

export function VenueMap({ lat, lon, venueName, className = '' }: VenueMapProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  
  if (!apiKey) {
    return (
      <div className={`bg-gray-100 rounded-lg p-6 ${className}`}>
        <div className="text-center mb-4">
          <MapPin className="w-12 h-12 mx-auto text-gray-400 mb-2" />
          <p className="text-sm font-semibold text-gray-700 mb-2">Google Maps API Key Not Configured</p>
        </div>
        <div className="bg-white rounded-md p-4 text-left space-y-3 text-sm text-gray-600">
          <div>
            <p className="font-medium text-gray-800 mb-1">📝 Add to your .env file:</p>
            <code className="block bg-gray-50 px-2 py-1 rounded border border-gray-200 text-xs font-mono">
              VITE_GOOGLE_MAPS_API_KEY=your_api_key_here<br />
              GOOGLE_MAPS_API_KEY=your_api_key_here
            </code>
          </div>
          <div>
            <p className="font-medium text-gray-800 mb-1">🔧 Setup in Google Cloud Console:</p>
            <ol className="list-decimal list-inside space-y-1 pl-2">
              <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">console.cloud.google.com</a></li>
              <li>Create or select a project</li>
              <li>Enable these APIs:
                <ul className="list-disc list-inside pl-4 mt-1">
                  <li>Maps Embed API</li>
                  <li>Geocoding API</li>
                  <li>Places API</li>
                </ul>
              </li>
              <li>Go to "Credentials" → "Create Credentials" → "API Key"</li>
              <li>Copy the key and add it to your .env file</li>
              <li>Restart your dev server after updating .env</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  // Using Google Maps Embed API (simpler than JS API, no additional setup needed)
  const mapUrl = `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${lat},${lon}&zoom=15`;

  return (
    <div className={`relative rounded-lg overflow-hidden ${className}`} style={{ height: '400px' }}>
      <iframe
        width="100%"
        height="100%"
        style={{ border: 0, display: 'block' }}
        loading="lazy"
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
        src={mapUrl}
        title={`Map of ${venueName}`}
      />
      <div className="absolute top-4 left-4 bg-white px-3 py-1.5 rounded-md shadow-md flex items-center gap-2 pointer-events-none">
        <MapPin className="w-4 h-4 text-primary-600" />
        <span className="text-sm font-medium text-gray-700">
          {Number(lat).toFixed(6)}, {Number(lon).toFixed(6)}
        </span>
      </div>
    </div>
  );
}
